/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Sunday, 17th February 2019
 *
 * Address Form
 *
 * Fields:  Street Address, Address2, City, State,  Zip
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import EventListener from 'react-event-listener';
import TextFieldWithValidation from 'components/InputFields/TextFieldWithValidation';
import withGoogleMaps from 'containers/services/withGoogleMaps';
import { debounce } from 'lodash';
import { inject, observer } from 'mobx-react';

// Material UI Components
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import SearchIcon from '@material-ui/icons/Search';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import parsePlacesAddressComponent from 'utils/parsePlacesAddressComponent';
import stateCodes from 'utils/stateCodes';

class AddressForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      address1: '',
      address1IsInvalid: false,
      address2: '',
      address2IsInvalid: false,
      address3: '',
      address3IsInvalid: false,
      city: '',
      cityIsInvalid: false,
      state: '',
      stateIsInvalid: false,
      zip: '',
      zipIsInvalid: false,
      isLoading: false,
      isOpen: false,
      anchorEl: null,
      popperWidth: 0,
      suggestions: [],
      addressComponents: {},
      biasResults: false,
    };
    this.handleAddress1Change = this.handleAddress1Change.bind(this);
    this.handleAddress2Change = this.handleAddress2Change.bind(this);
    this.handleAddress3Change = this.handleAddress3Change.bind(this);
    this.handleCityChange = this.handleCityChange.bind(this);
    this.handleStateChange = this.handleStateChange.bind(this);
    this.handleZipChange = this.handleZipChange.bind(this);
    this.debouncePredictions = debounce(this.fetchPredictions, 210);
  }

  handleAddress1Change = (e, isInvalid) => {
    this.setState({ address1: e.target.value, address1IsInvalid: isInvalid });

    if (this.props.handleInputUpdate) {
      this.props.handleInputUpdate(e, isInvalid);
    }
    this.onChange(e);
  };

  handleAddress2Change = (e, isInvalid) => {
    this.setState({ address2: e.target.value, address2IsInvalid: isInvalid });

    if (this.props.handleInputUpdate) {
      this.props.handleInputUpdate(e, isInvalid);
    }
  };
  handleAddress3Change = (e, isInvalid) => {
    this.setState({ address3: e.target.value, address3IsInvalid: isInvalid });

    if (this.props.handleInputUpdate) {
      this.props.handleInputUpdate(e, isInvalid);
    }
  };
  handleCityChange = (e, isInvalid) => {
    this.setState({ city: e.target.value, cityIsInvalid: isInvalid });

    if (this.props.handleInputUpdate) {
      this.props.handleInputUpdate(e, isInvalid);
    }
  };
  handleStateChange = (e, isInvalid) => {
    this.setState({ state: e.target.value, stateIsInvalid: isInvalid });

    if (this.props.handleInputUpdate) {
      this.props.handleInputUpdate(e, isInvalid);
    }
  };
  handleZipChange = (e, isInvalid) => {
    this.setState({ zip: e.target.value, zipIsInvalid: isInvalid });

    if (this.props.handleInputUpdate) {
      this.props.handleInputUpdate(e, isInvalid);
    }
  };

  handleBiasResults(biasResults) {
    this.setState({ biasResults: biasResults });
    this.props.enableGeoLocate(true);
  }

  onChange = e => {
    const value = e.target.value;
    if (!value) {
      this.setState({
        isOpen: false,
        suggestions: [],
      });
      return;
    }
    this.setState(
      {
        inputValue: value,
        anchorEl: e.target,
        popperWidth: e.target.clientWidth,
      },
      // setState callback
      () => (value.length > 3 ? this.debouncePredictions() : null)
    );
  };

  fetchPredictions = () => {
    const { address1 } = this.state;
    this.setState({ isLoading: true });
    if (address1.length) {
      this.setState({ isOpen: true });
      this.props
        .getAutoCompleteResults({
          ...this.props.searchOptions,
          input: address1,
        })
        .then((predictions, status) => this.autocompleteCallback(predictions, status))
        .catch(error => {
          this.handleClose();
          this.setState({ isLoading: false });
        });
    }
  };

  autocompleteCallback = predictions => {
    const highlightFirstSuggestion = false;
    this.setState({
      suggestions: predictions.map((p, idx) => ({
        id: p.id,
        description: p.description,
        placeId: p.place_id,
        active: highlightFirstSuggestion && idx === 0 ? true : false,
        index: idx,
        structuredFormatting: p.structured_formatting,
        matchedSubstrings: p.matched_substrings,
        terms: p.terms,
        types: p.types,
      })),
      isLoading: false,
    });
  };

  // Handles key pressed events
  onKeyDown = event => {
    if (event.key === 'Escape') {
      this.handleClose();
      this.clearSuggestions();
    }
  };

  handleClickAway = () => {
    this.handleClose();
  };

  handleClose = () => {
    this.setState({ isOpen: false });
  };

  clearSuggestions = () => {
    this.setState({ suggestions: [] });
  };

  clearAddressComponents = () => {
    this.setState({ addressComponents: {} });
  };

  handleSelect = index => {
    const suggestion = this.state.suggestions[index];
    const detailProps = {
      language: 'en',
      placeId: suggestion.placeId,
    };

    // Fetch selected suggestion address details
    this.props.getPlaceAddressComponents(detailProps).then(results => {
      // Update state
      this.setState(state => ({
        isOpen: !state.isOpen,
        addressComponents: results,
      }));
      this.onSelectUpdateFormFields(results);
    });
  };

  /**
   * Required to trigger a change event so MUI objects know that the input value was changed.
   */
  handleInputUpdateEvent = (input, value, eventType) => {
    // Get Current input value
    const lastInputValue = input.value;
    // Update input to new value
    input.value = value;

    // Create new input event if null, set type to change
    const inputEvent = new Event(eventType || 'change', { bubbles: true });

    // Set event as simulated (Note: may not be supported in future React versions)
    inputEvent.simulated = false;

    // Tracker
    const inputTracker = input._valueTracker;
    if (inputTracker) {
      inputTracker.setValue(lastInputValue);
    }

    // Trigger Event
    input.dispatchEvent(inputEvent);

    // Send event to imput handler if there is one
    if (this.props.handleInputUpdate) {
      this.props.handleInputUpdate(inputEvent, false);
    }
  };

  onSelectUpdateFormFields = results => {
    const address = parsePlacesAddressComponent(results);
    const prefix = this.props.prefix;

    var addrNumber = '';
    var addrStreet = '';
    var addrCity = '';
    var addrState = '';
    var addrZip = '';

    if (address && typeof address === 'object') {
      // Number
      if (address.hasOwnProperty('number')) {
        if (address.number.hasOwnProperty('longName')) {
          addrNumber = address.number.longName;
        } else if (address.number.hasOwnProperty('shortName')) {
          addrNumber = address.number.shortName;
        }
      }
      // Street
      if (address.hasOwnProperty('street')) {
        if (address.street.hasOwnProperty('longName')) {
          addrStreet = address.street.longName;
        } else if (address.street.hasOwnProperty('shortName')) {
          addrStreet = address.street.shortName;
        }
      }
      // City
      if (address.hasOwnProperty('city')) {
        if (address.city.hasOwnProperty('longName')) {
          addrCity = address.city.longName;
        } else if (address.city.hasOwnProperty('shortName')) {
          addrCity = address.city.shortName;
        }
      }
      // State
      if (address.hasOwnProperty('state')) {
        if (address.state.hasOwnProperty('shortName')) {
          addrState = address.state.shortName;
        }
      }
      // Zip
      if (address.hasOwnProperty('zip')) {
        if (address.zip.hasOwnProperty('longName')) {
          addrZip = address.zip.longName;
        } else if (address.zip.hasOwnProperty('shortName')) {
          addrZip = address.zip.shortName;
        }
      }
    }
    const fullStreetAddress = (addrNumber + ' ' + addrStreet).replace(/^\s+|\s+$|\s+(?=\s)/g, '');

    // Update Form State from user selection
    this.setState({
      address1: fullStreetAddress,
      address2: '',
      city: addrCity,
      state: addrState,
      zip: addrZip,
    });

    // Check to see if a store was passed
    if (this.props.toStore) {
      this.props.store.setField(prefix + 'Address1', fullStreetAddress);
      this.props.store.setField(prefix + 'Address1IsInvalid', false);
      this.props.store.setField(prefix + 'City', addrCity);
      this.props.store.setField(prefix + 'CityIsInvalid', false);
      this.props.store.setField(prefix + 'State', addrState);
      this.props.store.setField(prefix + 'StateIsInvalid', false);
      this.props.store.setField(prefix + 'Zip', addrZip);
      this.props.store.setField(prefix + 'ZipIsInvalid', false);
    }

    //Update Street Address (Change event will trigger autocomplete, using input here)
    const streetInput = document.getElementById('address1');
    this.handleInputUpdateEvent(streetInput, fullStreetAddress, null);

    // Update City
    const cityInput = document.getElementById('city');
    this.handleInputUpdateEvent(cityInput, addrCity, 'change');

    // Update State
    const stateInput = document.getElementById('state');
    this.handleInputUpdateEvent(stateInput, addrState, 'change');

    // Update Zip
    const zipInput = document.getElementById('zip');
    this.handleInputUpdateEvent(zipInput, addrZip, 'change');
  };

  componentDidMount() {
    // Init Google Maps API
    this.props.init();
  }

  render() {
    const { classes, address1, address2, address3, city, state, zip } = this.props;
    const { suggestions, isOpen } = this.state;
    const prefix = this.props.prefix ? this.props.prefix : '';

    return (
      <Grid container justify='center' className={classes.formGridContainer}>
        <Grid item xs={12}>
          <EventListener target='address1' onkeydown={this.onKeyDown} />
          <EventListener target='state' onchange={this.handleStateChange} />
          <TextFieldWithValidation
            id='address1'
            name='Address1'
            label='Street Address'
            type='text'
            visible={address1}
            value={
              this.props.toStore
                ? this.props.store[prefix + 'Address1']
                : this.props.passValues
                ? this.props.address1Value
                : this.state.address1
            }
            handleInputChange={this.handleAddress1Change}
            required
            message='Street address is a required field.'
            className={classes.address1}
            fullWidth
            margin='dense'
            autoComplete='off'
            onClick={() => (!this.state.biasResults ? this.handleBiasResults(true) : null)}
            InputLabelProps={{
              className: classes.inputLabel,
            }}
            InputProps={{
              className: classes.inputText,
              endAdornment: (
                <InputAdornment>
                  {this.state.isLoading ? (
                    <CircularProgress size={20} className={classes.circularProgress} />
                  ) : (
                    <SearchIcon className={classes.searchIcon} />
                  )}
                </InputAdornment>
              ),
            }}
          />
          <Popper open={isOpen} disablePortal className={classes.address1Popper} anchorEl={this.state.anchorEl}>
            <Paper className={classes.address1Paper} style={{ width: this.state.popperWidth }}>
              <ClickAwayListener onClickAway={this.handleClickAway}>
                <MenuList className={classes.address1PopperMenuList}>
                  {suggestions.map(suggestion => {
                    const { id, index, description } = suggestion;
                    return (
                      <MenuItem
                        key={id}
                        component='div'
                        onClick={() => this.handleSelect(index)}
                        className={classes.address1MenuItem}
                      >
                        {description}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Popper>
          <TextFieldWithValidation
            id='address2'
            name='Address2'
            type='text'
            visible={address2}
            value={
              this.props.toStore
                ? this.props.store[prefix + 'Address2']
                : this.props.passValues
                ? this.props.address2Value
                : this.state.address2
            }
            handleInputChange={this.handleAddress2Change}
            label='P.O. Box / Suite / Apt. Number'
            className={classes.address2}
            fullWidth
            margin='dense'
            InputLabelProps={{
              className: classes.inputLabel,
            }}
          />
          <TextFieldWithValidation
            id='address3'
            name='Address3'
            type='text'
            visible={address3}
            value={
              this.props.toStore
                ? this.props.store[prefix + 'Address3']
                : this.props.passValues
                ? this.props.address3Value
                : this.state.address3
            }
            handleInputChange={this.handleAddress3Change}
            label='Other'
            className={classes.address3}
            fullWidth
            margin='dense'
            InputLabelProps={{
              className: classes.inputLabel,
            }}
          />
          <Grid item xs={12}>
            <Grid container>
              <Grid item sm={7} xs={12}>
                <TextFieldWithValidation
                  id='city'
                  name='City'
                  type='text'
                  visible={city}
                  message='City is a required field.'
                  value={
                    this.props.toStore
                      ? this.props.store[prefix + 'City']
                      : this.props.passValues
                      ? this.props.cityValue
                      : this.state.city
                  }
                  handleInputChange={this.handleCityChange}
                  required
                  label='City'
                  className={classes.addressCity}
                  fullWidth
                  margin='dense'
                  InputLabelProps={{
                    className: classes.inputLabel,
                  }}
                />
              </Grid>
              <Grid item sm={5} xs={11}>
                <Grid container>
                  <Grid item sm={5} xs={3}>
                    <TextFieldWithValidation
                      id='state'
                      type='text'
                      name='State'
                      visible={state}
                      message='State is a required field.'
                      handleInputChange={this.handleStateChange}
                      select
                      required
                      label='State'
                      className={classes.addressState}
                      fullWidth
                      margin='dense'
                      value={
                        this.props.toStore
                          ? this.props.store[prefix + 'State']
                          : this.props.passValues
                          ? this.props.stateValue
                          : this.state.state
                      }
                      InputLabelProps={{
                        className: classes.inputLabel,
                      }}
                      SelectProps={{
                        classes: {
                          icon: classes.selectIcon,
                        },
                        SelectDisplayProps: {
                          style: {
                            paddingRight: '20px',
                            textOverflow: 'clip',
                          },
                        },
                        MenuProps: {
                          style: {
                            maxHeight: '300px',
                          },
                        },
                      }}
                    >
                      {stateCodes.map(option => (
                        <MenuItem key={option.abbr} value={option.abbr} className={classes.menuItem}>
                          {option.abbr}
                        </MenuItem>
                      ))}
                    </TextFieldWithValidation>
                  </Grid>
                  <Grid item sm={7} xs={5}>
                    <TextFieldWithValidation
                      id='zip'
                      name='Zip'
                      type='text'
                      visible={zip}
                      message='A valid 5 or 9 digit zip code is required.'
                      value={
                        this.props.toStore
                          ? this.props.store[prefix + 'Zip']
                          : this.props.passValues
                          ? this.props.zipValue
                          : this.state.zip
                      }
                      handleInputChange={this.handleZipChange}
                      required
                      label='Zip'
                      // eslint-disable-next-line
                      pattern='^\d{5}(-\d{4})?$'
                      className={classes.addressZip}
                      fullWidth
                      margin='dense'
                      InputLabelProps={{
                        className: classes.inputLabel,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

AddressForm.propTypes = {
  classes: PropTypes.object.isRequired,
  address1: PropTypes.bool,
  address2: PropTypes.bool,
  address3: PropTypes.bool,
  city: PropTypes.bool,
  state: PropTypes.bool,
  zip: PropTypes.bool,
  address1Value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  address2Value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  address3Value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  cityValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  stateValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  zipValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  toStore: PropTypes.bool,
  store: (props, propName, componentName) => {
    if (props['toStore'] && (props[propName] === undefined || typeof props[propName] !== 'object')) {
      return new Error('textFieldProps store is a required prop if toStore props are true.');
    }
  },
  prefix: PropTypes.string,
  handleInputUpdate: PropTypes.func,
};

const styles = theme => ({
  menuItem: {
    paddingTop: '1px',
    paddingBottom: '1px',
  },
  selectIcon: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '0',
    },
  },
  circularProgress: {},
  searchIcon: {
    color: theme.palette.type === 'light' ? 'rgb(128, 128, 128)' : theme.palette.common.white,
    padding: '0',
  },
  address1Paper: {
    backgroundColor: theme.palette.background.paper,
  },
  address1Popper: {
    zIndex: 999,
    marginTop: 0,
  },
  address1PopperMenuList: {},
  address1MenuItem: {
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    marginTop: 1,
    marginBottom: 1,
    paddingTop: 3,
    paddingBottom: 3,
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      paddingTop: 3,
      paddingBottom: 3,
      paddingLeft: 2,
      paddingRight: 2,
      fontSize: '10pt',
    },
  },
  address1: {
    padding: '2px',
  },
  address2: {
    padding: '2px',
  },
  address3: {
    padding: '2px',
  },
  addressCity: {
    padding: '2px',
  },
  addressState: {
    padding: '2px',
  },
  addressZip: {
    padding: '2px',
  },
  inputLabel: {
    fontSize: '11pt',
    [theme.breakpoints.down('xs')]: {
      fontSize: '10pt',
    },
  },
  formGridContainer: {},
});

export default withStyles(styles, { withTheme: true })(
  inject('registrationStore')(withGoogleMaps(observer(AddressForm)))
);
